import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { EmployerHubLayout } from "../../components/hub/EmployerHubLayout";
import { HubEmptyPanel } from "../../components/hub/HubEmptyPanel";
import { Badge } from "../../components/ui/badge";
import { Receipt, ExternalLink } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

const formatAmount = (cents, currency) =>
  cents == null
    ? "—"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: currency || "CAD" }).format(cents / 100);

const PAYMENT_BADGE = {
  paid: "text-emerald-700 border-emerald-200 bg-emerald-50",
  pending: "text-amber-700 border-amber-200 bg-amber-50",
  failed: "text-red-700 border-red-200 bg-red-50",
  refunded: "text-gray-600 border-gray-200 bg-gray-50",
};

export default function EmployerHubBilling() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/api/employer/hub/billing")
      .then((res) => setData(res.data))
      .catch(() => setError("Unable to load billing history"));
  }, []);

  const purchases = data?.purchases || [];

  return (
    <EmployerHubLayout>
      <div data-testid="employer-hub-billing">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Billing</h1>
        <p className="mt-2 text-gray-500">Your Inter-View purchases and receipts.</p>

        {error && (
          <div className="mt-8 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>
        )}

        {!data && !error && (
          <div className="mt-10 h-40 bg-gray-100 rounded-2xl animate-pulse" data-testid="hub-billing-loading" />
        )}

        {data && purchases.length === 0 && (
          <div className="mt-10">
            <HubEmptyPanel
              icon={Receipt}
              testid="hub-billing-empty"
              title="No purchases yet"
              body="When you purchase an Employer Intake Package, the payment record and receipt will appear here."
            />
          </div>
        )}

        {purchases.length > 0 && (
          <div className="mt-10 space-y-4" data-testid="hub-billing-list">
            {purchases.map((p, i) => (
              <div
                key={p.service_reference}
                className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6"
                data-testid={`billing-row-${i}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900" data-testid={`billing-package-${i}`}>
                        {p.package_name}
                      </h3>
                      {p.is_seed && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" data-testid={`billing-seed-badge-${i}`}>
                          Test data
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500" data-testid={`billing-reference-${i}`}>
                      Ref {p.service_reference} · Purchased {formatDate(p.purchased_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6">
                    <span className="text-base font-semibold text-gray-900" data-testid={`billing-amount-${i}`}>
                      {formatAmount(p.amount_cents, p.currency)}
                    </span>
                    <Badge
                      variant="outline"
                      className={`capitalize font-normal ${PAYMENT_BADGE[p.payment_status] || "text-gray-600 border-gray-200"}`}
                      data-testid={`billing-status-${i}`}
                    >
                      {p.payment_status || "unknown"}
                    </Badge>
                    {p.receipt_url ? (
                      <a
                        href={p.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                        data-testid={`billing-receipt-${i}`}
                      >
                        Receipt
                        <ExternalLink className="ml-1 h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400" data-testid={`billing-no-receipt-${i}`}>
                        No receipt available
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {purchases.length > 0 && (
          <p className="mt-6 text-xs text-gray-400" data-testid="billing-readonly-note">
            Billing is read-only in your Hub. For payment questions, reply to your purchase confirmation email.
          </p>
        )}
      </div>
    </EmployerHubLayout>
  );
}
